<template>
  <div class="service-input-form-container">
    <div class="order-breadcrumb">
      <router-link to="/service">설문 주문</router-link>
      <span class="order-breadcrumb-sep">›</span>
      <span>주문 상세</span>
    </div>

    <div class="order-head">
      <h1 class="order-title">설문 정보를 알려주세요</h1>
      <ol class="order-steps">
        <li class="order-step">1 견적</li>
        <li class="order-step-line"></li>
        <li class="order-step order-step--active">2 설문 정보</li>
        <li class="order-step-line"></li>
        <li class="order-step">3 주문 완료</li>
      </ol>
    </div>

    <div class="order-grid">
      <div class="order-col-left" ref="orderColLeft">
        <OptionInfoVue />
        <SurveyInfoVue />
        <PaymentOption />
      </div>
      <div class="order-col-right" ref="orderColRight">
        <div class="order-pay-card" ref="orderPayCard">
          <PaymentMethodVue />
          <PaymentInfo />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OptionInfoVue from '@/components/Service/ServiceInputForm/OptionInfo.vue'
import PaymentMethodVue from '@/components/Service/ServiceInputForm/PaymentMethod.vue'
import SurveyInfoVue from '@/components/Service/ServiceInputForm/SurveyInfo.vue'
import PaymentOption from '@/components/Service/ServiceInputForm/PaymentOption.vue'
import PaymentInfo from '@/components/Service/ServiceInputForm/PaymentInfo.vue'

export default {
  name: 'ServiceInputForm',
  components: {
    OptionInfoVue,
    PaymentMethodVue,
    SurveyInfoVue,
    PaymentOption,
    PaymentInfo
  },
  mounted() {
    window.scrollTo(0,0)
    this.measurePayCardLayout()
    this.applyPayCardPosition()
    // 폰트 로딩 등으로 마운트 직후 높이가 미세하게 더 바뀌는 경우가 있어 재측정
    setTimeout(() => { this.measurePayCardLayout(); this.applyPayCardPosition() }, 300)
    window.addEventListener('resize', this.onPayCardResize)
    window.addEventListener('scroll', this.onPayCardScroll, { passive: true })
    const leftEl = this.$refs.orderColLeft
    if (leftEl && window.ResizeObserver) {
      this.payCardResizeObserver = new ResizeObserver(() => this.onPayCardResize())
      this.payCardResizeObserver.observe(leftEl)
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onPayCardResize)
    window.removeEventListener('scroll', this.onPayCardScroll)
    if (this.payCardResizeObserver) this.payCardResizeObserver.disconnect()
    if (this.payCardRaf) cancelAnimationFrame(this.payCardRaf)
  },
  methods: {
    // position:sticky가 이 환경에서 히어로 때와 같은 이유로 불안정해서
    // (grid 자식 + sticky 조합), 결제 카드도 스크롤에 맞춰 직접 translateY를
    // 계산한다. 헤더 아래(200px)에서 붙고, 왼쪽 폼이 끝나는 지점에서 같이 멈춘다.
    //
    // 레이아웃 측정(getBoundingClientRect/offsetHeight)은 리사이즈·폼 높이
    // 변화가 있을 때만 하고 캐시해둔다 — 스크롤마다 큰 폼 전체 레이아웃을
    // 다시 읽으면 강제 리플로우가 매 프레임 걸려 카드가 뚝뚝 끊기며 스크롤을
    // 못 따라오는 버그가 있었다. 스크롤 핸들러는 캐시값 + scrollY만 쓴다.
    measurePayCardLayout() {
      const leftEl = this.$refs.orderColLeft
      const cardEl = this.$refs.orderPayCard
      if (!leftEl || !cardEl) return
      this.payCardLayout = {
        containerTop: leftEl.getBoundingClientRect().top + window.scrollY,
        leftHeight: leftEl.offsetHeight,
        cardHeight: cardEl.offsetHeight
      }
    },
    onPayCardResize() {
      this.measurePayCardLayout()
      this.applyPayCardPosition()
    },
    onPayCardScroll() {
      if (this.payCardRaf) return
      this.payCardRaf = requestAnimationFrame(() => {
        this.payCardRaf = null
        this.applyPayCardPosition()
      })
    },
    applyPayCardPosition() {
      const rightEl = this.$refs.orderColRight
      if (!rightEl || !this.payCardLayout) return

      if (window.innerWidth <= 900) {
        rightEl.style.transform = ''
        return
      }

      const stickyTop = 200
      const { containerTop, leftHeight, cardHeight } = this.payCardLayout
      const maxOffset = Math.max(0, leftHeight - cardHeight)

      let offset = window.scrollY + stickyTop - containerTop
      offset = Math.min(Math.max(offset, 0), maxOffset)

      rightEl.style.transform = `translateY(${offset}px)`
    }
  }
}
</script>

<style scoped>
.service-input-form-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 40px 24px 80px;
  background-color: var(--bg-page);
  font-family: var(--font-sans);
}

.order-breadcrumb {
  text-align: left;
  font-size: 13px;
  color: var(--text-muted);
  margin-bottom: var(--space-4);
}

.order-breadcrumb a {
  color: var(--text-muted);
  text-decoration: none;
}

.order-breadcrumb-sep {
  margin: 0 6px;
}

.order-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin-bottom: var(--space-8);
}

.order-title {
  margin: 0;
  font-size: 28px;
  font-weight: var(--font-weight-bold);
  color: var(--text-heading);
  text-align: left;
}

.order-steps {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: var(--space-3);
}

.order-step {
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--ink-300);
}

.order-step--active {
  color: var(--green-600);
  font-weight: var(--font-weight-semibold);
}

.order-step-line {
  width: 24px;
  height: 1px;
  background-color: var(--ink-200);
}

.order-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  align-items: start;
  gap: 32px;
}

.order-col-left :deep(.option-info-container),
.order-col-left :deep(.survey-info-container),
.order-col-left :deep(.payment-option-container) {
  max-width: 100%;
  width: 100%;
  margin: 0 0 20px;
}

.order-col-left :deep(.payment-option-container) {
  margin-bottom: 0;
}

.order-col-right {
  /* transform은 JS(updatePayCardPosition)가 스크롤에 맞춰 직접 지정한다 */
  will-change: transform;
}

.order-pay-card {
  background-color: var(--surface-card);
  border: 1px solid var(--ink-100);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.order-col-right :deep(.payment-container),
.order-col-right :deep(.pay-info-container) {
  max-width: 100%;
  width: 100%;
  margin: 0;
  padding: 24px;
  border: none;
  border-radius: 0;
  box-shadow: none;
  background-color: transparent;
}

.order-col-right :deep(.pay-info-container) {
  padding-top: 0;
}

.order-col-right :deep(.pay-btn) {
  max-width: 100%;
}

@media (max-width: 900px) {
  .order-grid {
    grid-template-columns: 1fr;
  }

  .order-col-right {
    position: static;
  }
}

@media (max-width: 560px) {
  .order-title {
    font-size: 22px;
  }
}
</style>
